import { Lightning, Registry } from '@lightningjs/sdk'

import { getColor, grey960 } from '../../constants/colors'
import { HIDDEN } from '../TvCards/templates'
import QwertyKeyboardRow from './QwertyKeyboardRow'
import { createKbLayout, keyboardLayouts, QwertyKey } from './utils'

export class QwertyKeyboard extends Lightning.Component {
  private _rowIndex = 0
  private _keyHandler = null

  static _template() {
    return {
      w: 840,
      h: 450,
      rect: true,
      color: getColor(grey960),
      alpha: HIDDEN,
      flex: {
        direction: 'column',
        justifyContent: 'center',
      },
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: 2,
      },
    }
  }

  get sectionText() {
    return 'Keyboard'
  }

  get rowIndex() {
    return this._rowIndex
  }

  set rowIndex(v: number) {
    this._rowIndex = v
  }

  get currentRow() {
    return this.childList.getAt(this.rowIndex) as QwertyKeyboardRow
  }

  get totalRows() {
    return this.childList.length
  }

  get isFocusOnFirstRow() {
    return this.rowIndex === 0
  }

  get isFocusOnLastRow() {
    return this.rowIndex === this.totalRows - 1
  }

  get isFocusOnPenultimateRow() {
    return this.rowIndex === this.totalRows - 2
  }

  _init() {
    this.patch({ children: createKbLayout(keyboardLayouts['lowercase']) })
  }

  _getFocused() {
    return this.currentRow
  }

  _focus() {
    this.patch({
      smooth: {
        alpha: 1,
      },
    })

    this.attachVizioKbListener()
  }

  _unfocus() {
    this.patch({
      smooth: {
        alpha: HIDDEN,
      },
    })

    this.dettachVizioKbListener()
  }

  _handleDown() {
    if (this.rowIndex === this.totalRows - 1) {
      return false
    } else {
      this.nextRow()
    }
  }

  _handleUp() {
    if (this.rowIndex === 0) {
      this.fireAncestors('$unfocusVizioKeyboard')
    } else {
      this.prevRow()
    }
  }

  _handleBack() {
    return true
  }

  nextRow() {
    const currentKeyIndex = this.currentRow.keyIndex
    const nextRow = this.getRowByIndex(this.rowIndex + 1)

    if (this.isFocusOnPenultimateRow) {
      if (currentKeyIndex >= 0 && currentKeyIndex <= 3) {
        nextRow.keyIndex = 0
      } else if (currentKeyIndex >= 4 && currentKeyIndex <= 7) {
        nextRow.keyIndex = 1
      } else {
        nextRow.keyIndex = 2
      }
    } else {
      nextRow.keyIndex = currentKeyIndex
    }

    this._rowIndex++
  }

  prevRow() {
    const currentKeyIndex = this.currentRow.keyIndex
    const prevRow = this.getRowByIndex(this.rowIndex - 1)

    if (this.isFocusOnLastRow) {
      if (currentKeyIndex === 0) {
        prevRow.keyIndex = 1
      } else if (currentKeyIndex === 1) {
        prevRow.keyIndex = 5
      } else {
        prevRow.keyIndex = 9
      }
    } else {
      prevRow.keyIndex = currentKeyIndex
    }

    this._rowIndex--
  }

  getRowByIndex(i: number) {
    return this.childList.getAt(i) as QwertyKeyboardRow
  }

  $onSoftKey(key: QwertyKey) {
    const { toggle, label, disabled } = key

    if (disabled) return

    if (toggle) {
      const layout = keyboardLayouts[toggle]
      const keyIndex = this.currentRow.keyIndex
      if (layout) {
        this.patch({ children: createKbLayout(layout) })
        this.currentRow.keyIndex = keyIndex
        return
      }
    }

    this.fireAncestors('$onVizioKeyboardKeyPress', label)
  }

  attachVizioKbListener() {
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Backspace') {
        this.fireAncestors('$onVizioKeyboardKeyPress', e.key)
        e.stopImmediatePropagation()
        return
      }

      // Allow only printable ASCII (space through ~) and Space key
      const isAllowed = /^[\x20-\x7E]$/.test(e.key) || e.key === 'Space'

      if (isAllowed) {
        this.fireAncestors('$onVizioKeyboardKeyPress', e.key)
      }
    }

    Registry.addEventListener(document, 'keydown', this._keyHandler)
  }

  dettachVizioKbListener() {
    if (this._keyHandler) {
      Registry.removeEventListener(document, 'keydown', this._keyHandler)
      this._keyHandler = null
    }
  }
}
